import { useEffect, useState } from "react";
import {
  calculateCompleteProration,
  getBillingPeriodDates,
  resolveEntityId,
} from "../../utils/prorationUtils";
import "./_upsell-review-step.scss";

const normalizePrice = (price) => Math.round((parseFloat(price) || 0) * 100) / 100;

// D14 - matches `resolveEntityId`'s reasoning in prorationUtils.js: the
// "current subscription" side of this comparison can carry `_id` populated
// to the full catalog entity while the "new upsell" side always carries a
// bare id string, so a direct `old._id === newItem._id` never matches and
// this fell through to name matching for every item - fine when it
// happened to work, silently wrong (mislabels an unchanged item "New" or
// pairs it with the wrong item by a name collision) when it didn't. Name
// compared case/whitespace-insensitively for the same reason
// `calculateProrationForItems` does.
const sameName = (x, y) => !!x && !!y && String(x).trim().toLowerCase() === String(y).trim().toLowerCase();

// Exported for tests only (D14 regression coverage) - every real consumer
// stays the component's own default export.
export function findMatch(newItem, oldItems, itemType) {
  if (!oldItems?.length) return null;
  const newId = resolveEntityId(newItem);
  return (
    oldItems.find((old) => {
      const oldId = resolveEntityId(old);
      if (oldId && newId && oldId === newId) return true;
      if (itemType === "plans") return sameName(old.planName, newItem.planName);
      if (itemType === "products") return sameName(old.productName, newItem.productName);
      if (itemType === "lineItems" || itemType === "taxes") return sameName(old.name, newItem.name);
      return false;
    }) || null
  );
}

export function getItemStatus(newItem, oldItems, itemType) {
  const oldItem = findMatch(newItem, oldItems, itemType);
  if (!oldItem) return "new";

  if (itemType === "plans" || itemType === "products") {
    const oldPrice = normalizePrice(oldItem.accordionData?.totalPrice ?? oldItem.amount ?? oldItem.price);
    const newPrice = normalizePrice(newItem.accordionData?.totalPrice ?? newItem.amount ?? newItem.price);
    const oldQty = oldItem.accordionData?.quantity || 1;
    const newQty = newItem.accordionData?.quantity || 1;

    if (itemType === "plans") {
      // `oldItem` is already `findMatch`'s result for `newItem`, so this is
      // always the same plan by definition - re-deriving it with the same
      // unwrapped-id/name comparison `findMatch` uses (rather than the raw
      // `_id === _id` this used to do) just keeps that true instead of
      // occasionally re-failing the same populated-vs-string-id mismatch
      // findMatch was fixed for, which would otherwise silently skip the
      // "Upgraded" label on a genuine upgrade.
      const samePlan = !!findMatch(newItem, [oldItem], "plans");
      if (samePlan && newPrice > oldPrice) return "upgraded";

      const oldBundleId = oldItem.bundle?._id || oldItem.bundle;
      const newBundleId = newItem.bundle?._id || newItem.bundle;
      if (oldBundleId === newBundleId && (newItem.bundlePosition || 0) > (oldItem.bundlePosition || 0)) {
        return "upgraded";
      }
    }
    if (oldPrice !== newPrice || oldQty !== newQty) return "modified";
  } else if (itemType === "lineItems") {
    if (normalizePrice(oldItem.totalPrice ?? oldItem.price) !== normalizePrice(newItem.totalPrice ?? newItem.price)) {
      return "modified";
    }
  } else if (itemType === "taxes") {
    if (normalizePrice(oldItem.totalTax ?? oldItem.amount) !== normalizePrice(newItem.totalTax ?? newItem.amount)) {
      return "modified";
    }
  }
  return "unchanged";
}

function getModifiedFields(newItem, oldItems, itemType) {
  const oldItem = findMatch(newItem, oldItems, itemType);
  if (!oldItem) return {};

  const modified = {};
  if (itemType === "plans" || itemType === "products") {
    const oldPrice = normalizePrice(oldItem.accordionData?.totalPrice ?? oldItem.amount ?? oldItem.price);
    const newPrice = normalizePrice(newItem.accordionData?.totalPrice ?? newItem.amount ?? newItem.price);
    if (oldPrice !== newPrice) modified.price = true;
    if ((oldItem.accordionData?.quantity || 1) !== (newItem.accordionData?.quantity || 1)) modified.quantity = true;
  } else if (itemType === "lineItems") {
    if (normalizePrice(oldItem.totalPrice ?? oldItem.price) !== normalizePrice(newItem.totalPrice ?? newItem.price)) {
      modified.price = true;
    }
  } else if (itemType === "taxes") {
    if (normalizePrice(oldItem.totalTax ?? oldItem.amount) !== normalizePrice(newItem.totalTax ?? newItem.amount)) {
      modified.amount = true;
    }
  }
  return modified;
}

const STATUS_LABEL = { new: "New", modified: "Modified", upgraded: "Upgraded", unchanged: "Unchanged" };

const FieldValue = ({ value, isModified, isPrice }) => {
  const formatted = isPrice ? (parseFloat(value) || 0).toFixed(2) : value;
  return isModified ? <strong className="upsell-review-step__modified-value">{formatted}</strong> : formatted;
};

const ItemCard = ({ name, priceLabel, qtyLabel, status }) => (
  <div className={`upsell-review-step__item-card upsell-review-step__item-card--${status || "plain"}`}>
    <div className="upsell-review-step__item-name">{name}</div>
    <div className="upsell-review-step__item-details">
      <span>{priceLabel}</span>
      {qtyLabel && <span>{qtyLabel}</span>}
    </div>
    {status && (
      <div className={`upsell-review-step__status-badge upsell-review-step__status-badge--${status}`}>
        {STATUS_LABEL[status]}
      </div>
    )}
  </div>
);

function getCurrentSubscriptionTotal(subscription) {
  const plansTotal = (subscription.plans || []).reduce(
    (sum, plan) => sum + (parseFloat(plan?.accordionData?.totalPrice) || parseFloat(plan?.amount) || 0),
    0
  );
  const productsTotal = (subscription.products || []).reduce(
    (sum, product) => sum + (parseFloat(product?.accordionData?.totalPrice) || parseFloat(product?.price) || 0),
    0
  );
  const lineItemsTotal = (subscription.lineItems || []).reduce(
    (sum, item) => sum + (parseFloat(item?.totalPrice) || parseFloat(item?.price) || 0),
    0
  );
  let discount = 0;
  if (subscription.discount) {
    const subtotal = plansTotal + productsTotal + lineItemsTotal;
    discount =
      subscription.discount.type === "Percent"
        ? ((parseFloat(subscription.discount.value) || 0) / 100) * subtotal
        : parseFloat(subscription.discount.value) || 0;
  }
  const taxesTotal = (subscription.taxes || []).reduce(
    (sum, tax) => sum + (parseFloat(tax?.totalTax) || parseFloat(tax?.amount) || 0),
    0
  );
  return (plansTotal + productsTotal + lineItemsTotal - discount + taxesTotal).toFixed(2);
}

/**
 * Step 4 - side-by-side comparison of the current subscription vs. the new
 * upsell/downsell billing items, plus the proration breakdown. Ported from
 * the old UpsellReview's comparison logic (byte-for-byte math, restyled to
 * BEM), which has no react-hook-form fields of its own to migrate - it's
 * pure derived display over `selectedSubscription`/`billingItemsData`.
 */
const UpsellReviewStep = ({ watch, selectedSubscription, billingItemsData, totalAmountValue }) => {
  const [prorationData, setProrationData] = useState(null);
  const title = watch("title");
  const description = watch("description");
  const company = watch("company");
  const type = watch("type") || "Upsell";

  useEffect(() => {
    if (!selectedSubscription || !billingItemsData) {
      setProrationData(null);
      return;
    }
    try {
      const { periodStart, periodEnd, changeDate } = getBillingPeriodDates(selectedSubscription);
      setProrationData(
        calculateCompleteProration({
          periodStart,
          periodEnd,
          changeDate,
          oldSubscription: selectedSubscription,
          newBillingItems: billingItemsData,
        })
      );
    } catch (error) {
      setProrationData(null);
    }
  }, [selectedSubscription, billingItemsData]);

  if (!selectedSubscription || !billingItemsData) {
    return (
      <div className="upsell-review-step">
        <p className="upsell-review-step__loading">Loading comparison data...</p>
      </div>
    );
  }

  const oldItems = {
    plans: selectedSubscription.plans || [],
    products: selectedSubscription.products || [],
    lineItems: selectedSubscription.lineItems || [],
    discount: selectedSubscription.discount || { type: "Flat", value: "0" },
    taxes: selectedSubscription.taxes || [],
  };

  const currencySign =
    selectedSubscription?.marketId?.currency?.[0]?.sign ||
    selectedSubscription?.market?.currency?.[0]?.sign ||
    "$";

  const discountStatus = (() => {
    if (!oldItems.discount || oldItems.discount.value === "0") return "new";
    if (
      normalizePrice(oldItems.discount.value) !== normalizePrice(billingItemsData.discount.value) ||
      oldItems.discount.type !== billingItemsData.discount.type
    ) {
      return "modified";
    }
    return "unchanged";
  })();

  return (
    <div className="upsell-review-step">
      <div className="upsell-review-step__header">
        <h3>{type} review</h3>
        <div className="upsell-review-step__meta">
          {title && (
            <div className="upsell-review-step__meta-item">
              <span className="upsell-review-step__meta-label">Title:</span> {title}
            </div>
          )}
          {description && (
            <div className="upsell-review-step__meta-item">
              <span className="upsell-review-step__meta-label">Description:</span> {description}
            </div>
          )}
          <div className="upsell-review-step__meta-item">
            <span className="upsell-review-step__meta-label">Current subscription:</span>{" "}
            {selectedSubscription.subscriptionName}
          </div>
          <div className="upsell-review-step__meta-item">
            <span className="upsell-review-step__meta-label">Billing cycle:</span>{" "}
            {selectedSubscription.billingCycle}
          </div>
          <div className="upsell-review-step__meta-item">
            <span className="upsell-review-step__meta-label">Customer:</span>{" "}
            {company?.companyName || company?.individualName || "N/A"}
          </div>
          <div className="upsell-review-step__meta-item">
            <span className="upsell-review-step__meta-label">Total amount:</span> {currencySign}{" "}
            {totalAmountValue || "0.00"}
          </div>
        </div>
      </div>

      <div className="upsell-review-step__legend">
        {Object.entries(STATUS_LABEL).map(([key, label]) => (
          <div key={key} className="upsell-review-step__legend-item">
            <span className={`upsell-review-step__status-badge upsell-review-step__status-badge--${key}`}>
              {label}
            </span>
          </div>
        ))}
      </div>

      <div className="upsell-review-step__columns">
        <div className="upsell-review-step__column">
          <h4>Current subscription</h4>

          <h5>Plans</h5>
          {oldItems.plans.length === 0 && <p className="upsell-review-step__empty">No plans</p>}
          {oldItems.plans.map((plan, i) => (
            <ItemCard
              key={plan._id || i}
              name={plan.planName || plan.name}
              priceLabel={`Price: $${parseFloat(plan.accordionData?.totalPrice || plan.amount || 0).toFixed(2)}`}
              qtyLabel={`Qty: ${plan.accordionData?.quantity || 1}`}
            />
          ))}

          <h5>Products</h5>
          {oldItems.products.length === 0 && <p className="upsell-review-step__empty">No products</p>}
          {oldItems.products.map((product, i) => (
            <ItemCard
              key={product._id || i}
              name={product.productName || product.name}
              priceLabel={`Price: $${parseFloat(product.accordionData?.totalPrice || product.price || 0).toFixed(2)}`}
              qtyLabel={`Qty: ${product.accordionData?.quantity || 1}`}
            />
          ))}

          <h5>Line items</h5>
          {oldItems.lineItems.length === 0 && <p className="upsell-review-step__empty">No line items</p>}
          {oldItems.lineItems.map((item, i) => (
            <ItemCard
              key={item._id || i}
              name={item.name}
              priceLabel={`Price: $${parseFloat(item.totalPrice || item.price || 0).toFixed(2)}`}
            />
          ))}

          <h5>Discount</h5>
          <ItemCard
            name={`${oldItems.discount.type} discount`}
            priceLabel={`Value: ${parseFloat(oldItems.discount.value || 0).toFixed(2)}`}
          />

          <h5>Taxes</h5>
          {oldItems.taxes.length === 0 && <p className="upsell-review-step__empty">No taxes</p>}
          {oldItems.taxes.map((tax, i) => (
            <ItemCard
              key={tax._id || i}
              name={tax.name}
              priceLabel={`Amount: $${parseFloat(tax.totalTax || tax.amount || 0).toFixed(2)}`}
            />
          ))}

          <h5>Previous total amount</h5>
          <ItemCard name="Total" priceLabel={`${currencySign} ${getCurrentSubscriptionTotal(selectedSubscription)}`} />
        </div>

        <div className="upsell-review-step__column">
          <h4>New {type.toLowerCase()} subscription</h4>

          <h5>Plans</h5>
          {(billingItemsData.plans || []).length === 0 && <p className="upsell-review-step__empty">No plans</p>}
          {(billingItemsData.plans || []).map((plan, i) => {
            const status = getItemStatus(plan, oldItems.plans, "plans");
            const modified = getModifiedFields(plan, oldItems.plans, "plans");
            return (
              <div key={plan._id || i} className={`upsell-review-step__item-card upsell-review-step__item-card--${status}`}>
                <div className="upsell-review-step__item-name">{plan.planName || plan.name}</div>
                <div className="upsell-review-step__item-details">
                  <span>
                    Price: $
                    <FieldValue value={plan.accordionData?.totalPrice || plan.amount || 0} isModified={modified.price} isPrice />
                  </span>
                  <span>
                    Qty: <FieldValue value={plan.accordionData?.quantity || 1} isModified={modified.quantity} />
                  </span>
                </div>
                <div className={`upsell-review-step__status-badge upsell-review-step__status-badge--${status}`}>
                  {STATUS_LABEL[status]}
                </div>
              </div>
            );
          })}

          <h5>Products</h5>
          {(billingItemsData.products || []).length === 0 && <p className="upsell-review-step__empty">No products</p>}
          {(billingItemsData.products || []).map((product, i) => {
            const status = getItemStatus(product, oldItems.products, "products");
            const modified = getModifiedFields(product, oldItems.products, "products");
            return (
              <div key={product._id || i} className={`upsell-review-step__item-card upsell-review-step__item-card--${status}`}>
                <div className="upsell-review-step__item-name">{product.productName || product.name}</div>
                <div className="upsell-review-step__item-details">
                  <span>
                    Price: $
                    <FieldValue value={product.accordionData?.totalPrice || product.price || 0} isModified={modified.price} isPrice />
                  </span>
                  <span>
                    Qty: <FieldValue value={product.accordionData?.quantity || 1} isModified={modified.quantity} />
                  </span>
                </div>
                <div className={`upsell-review-step__status-badge upsell-review-step__status-badge--${status}`}>
                  {STATUS_LABEL[status]}
                </div>
              </div>
            );
          })}

          <h5>Line items</h5>
          {(billingItemsData.lineItems || []).length === 0 && <p className="upsell-review-step__empty">No line items</p>}
          {(billingItemsData.lineItems || []).map((item, i) => {
            const status = getItemStatus(item, oldItems.lineItems, "lineItems");
            const modified = getModifiedFields(item, oldItems.lineItems, "lineItems");
            return (
              <div key={item._id || i} className={`upsell-review-step__item-card upsell-review-step__item-card--${status}`}>
                <div className="upsell-review-step__item-name">{item.name}</div>
                <div className="upsell-review-step__item-details">
                  <span>
                    Price: $
                    <FieldValue value={item.totalPrice || item.price || 0} isModified={modified.price} isPrice />
                  </span>
                </div>
                <div className={`upsell-review-step__status-badge upsell-review-step__status-badge--${status}`}>
                  {STATUS_LABEL[status]}
                </div>
              </div>
            );
          })}

          <h5>Discount</h5>
          <div className={`upsell-review-step__item-card upsell-review-step__item-card--${discountStatus}`}>
            <div className="upsell-review-step__item-name">
              <FieldValue
                value={billingItemsData.discount.type}
                isModified={discountStatus === "modified" && oldItems.discount.type !== billingItemsData.discount.type}
              />{" "}
              discount
            </div>
            <div className="upsell-review-step__item-details">
              <span>
                Value:{" "}
                <FieldValue
                  value={billingItemsData.discount.value}
                  isModified={
                    discountStatus === "modified" &&
                    normalizePrice(oldItems.discount.value) !== normalizePrice(billingItemsData.discount.value)
                  }
                  isPrice
                />
              </span>
            </div>
            <div className={`upsell-review-step__status-badge upsell-review-step__status-badge--${discountStatus}`}>
              {STATUS_LABEL[discountStatus]}
            </div>
          </div>

          <h5>Taxes</h5>
          {(billingItemsData.taxes || []).length === 0 && <p className="upsell-review-step__empty">No taxes</p>}
          {(billingItemsData.taxes || []).map((tax, i) => {
            const status = getItemStatus(tax, oldItems.taxes, "taxes");
            const modified = getModifiedFields(tax, oldItems.taxes, "taxes");
            return (
              <div key={tax._id || i} className={`upsell-review-step__item-card upsell-review-step__item-card--${status}`}>
                <div className="upsell-review-step__item-name">{tax.name}</div>
                <div className="upsell-review-step__item-details">
                  <span>
                    Amount: $
                    <FieldValue value={tax.totalTax || tax.amount || 0} isModified={modified.amount} isPrice />
                  </span>
                </div>
                <div className={`upsell-review-step__status-badge upsell-review-step__status-badge--${status}`}>
                  {STATUS_LABEL[status]}
                </div>
              </div>
            );
          })}

          <h5>New total amount</h5>
          <ItemCard name="Total" priceLabel={`${currencySign} ${totalAmountValue || "0.00"}`} />

          {prorationData && (
            <div className="upsell-review-step__proration">
              <h5>Proration summary</h5>
              <div className="upsell-review-step__proration-info">
                <span>Proration ratio: {((parseFloat(prorationData.prorationRatio) || 0) * 100).toFixed(1)}%</span>
                <span>Period start: {new Date(prorationData.summary.periodStart).toLocaleDateString()}</span>
                <span>Period end: {new Date(prorationData.summary.periodEnd).toLocaleDateString()}</span>
                <span>Change date: {new Date(prorationData.summary.changeDate).toLocaleDateString()}</span>
              </div>
              {["plans", "products", "lineItems"].map(
                (section) =>
                  prorationData.breakdown[section].itemDetails.length > 0 && (
                    <div key={section} className="upsell-review-step__proration-section">
                      <h6>{section === "lineItems" ? "Line items" : section.charAt(0).toUpperCase() + section.slice(1)}:</h6>
                      {prorationData.breakdown[section].itemDetails.map((item, i) => (
                        <div key={i} className="upsell-review-step__proration-row">
                          <span>{item.itemName}</span>
                          <span>
                            {currencySign} {(parseFloat(item.proratedCharge) || 0).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )
              )}
              <div className="upsell-review-step__proration-total">
                <span>Total prorated amount:</span>
                <span>
                  {currencySign} {(parseFloat(prorationData.totalProratedAmount) || 0).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpsellReviewStep;
